/**
 * Enhanced Search - Universal Predictive Search Script v4
 *
 * This script extends Shopify's predictive search with fuzzy search capabilities.
 * It's designed to work across multiple theme types:
 * - Dawn-based themes (Shopify default)
 * - FoxTheme-based themes (and similar premium themes)
 *
 * Configuration (set via theme extension before this script loads):
 * - window.ENHANCED_SEARCH_ACTIVE: boolean - Enable/disable enhanced search
 * - window.ENHANCED_SEARCH_CLIENT_ID: string - Client ID for the search server
 * - window.ENHANCED_SEARCH_HIGHLIGHT: boolean - Enable/disable search term highlighting
 * - window.ENHANCED_SEARCH_TITLE_PREFIX: string - Optional prefix to remove from product titles
 * - window.ENHANCED_SEARCH_INCLUDE_OUT_OF_STOCK: boolean - Include out-of-stock products
 * - window.ENHANCED_SEARCH_SORT_IN_STOCK_FIRST: boolean - Sort in-stock products first
 * - window.ENHANCED_SEARCH_BOOST_KEYWORDS: string - Comma-separated keywords to boost
 */

// Search server base URL (same for all clients)
const ENHANCED_SEARCH_SERVER_URL = "https://enhanced-search-prod-lmkyt.ondigitalocean.app";

// Configuration with defaults
const ENHANCED_SEARCH_CONFIG = {
  active: typeof window.ENHANCED_SEARCH_ACTIVE === "boolean" ? window.ENHANCED_SEARCH_ACTIVE : false,
  //active: false,disables fuzzy search. delete this line and remove comments from line about to reactivate//
  clientId: window.ENHANCED_SEARCH_CLIENT_ID || null,
  highlightEnabled: typeof window.ENHANCED_SEARCH_HIGHLIGHT === "boolean" ? window.ENHANCED_SEARCH_HIGHLIGHT : true,
  titlePrefix: window.ENHANCED_SEARCH_TITLE_PREFIX || "",
  includeOutOfStock:
    typeof window.ENHANCED_SEARCH_INCLUDE_OUT_OF_STOCK === "boolean"
      ? window.ENHANCED_SEARCH_INCLUDE_OUT_OF_STOCK
      : false,
  sortInStockFirst:
    typeof window.ENHANCED_SEARCH_SORT_IN_STOCK_FIRST === "boolean" ? window.ENHANCED_SEARCH_SORT_IN_STOCK_FIRST : true,
  boostKeywords: window.ENHANCED_SEARCH_BOOST_KEYWORDS || "",
};

// Build the full API URL from server URL and client ID
ENHANCED_SEARCH_CONFIG.apiUrl = ENHANCED_SEARCH_CONFIG.clientId
  ? `${ENHANCED_SEARCH_SERVER_URL}/api/mt/${ENHANCED_SEARCH_CONFIG.clientId}`
  : null;

// Validate configuration
if (ENHANCED_SEARCH_CONFIG.active && !ENHANCED_SEARCH_CONFIG.clientId) {
  console.warn(
    "[Enhanced Search] ENHANCED_SEARCH_ACTIVE is true but ENHANCED_SEARCH_CLIENT_ID is not set. Enhanced search will be disabled.",
  );
  ENHANCED_SEARCH_CONFIG.active = false;
}

/**
 * Build the search URL with all query parameters
 */
function buildEnhancedSearchUrl(searchTerm) {
  const params = new URLSearchParams();
  params.set("q", searchTerm);

  if (ENHANCED_SEARCH_CONFIG.includeOutOfStock) {
    params.set("includeOutOfStock", "true");
  }

  if (ENHANCED_SEARCH_CONFIG.sortInStockFirst) {
    params.set("sortInStockFirst", "true");
  }

  if (ENHANCED_SEARCH_CONFIG.boostKeywords) {
    params.set("boostKeywords", ENHANCED_SEARCH_CONFIG.boostKeywords);
  }

  return `${ENHANCED_SEARCH_CONFIG.apiUrl}/search?${params.toString()}`;
}

/**
 * Helper function to highlight matching text in search results
 */
function highlightSearchText(text, query) {
  if (!ENHANCED_SEARCH_CONFIG.highlightEnabled || !query || !text) {
    console.log("[Enhanced Search] Highlight skipped:", {
      enabled: ENHANCED_SEARCH_CONFIG.highlightEnabled,
      query,
      hasText: !!text,
    });
    return text;
  }

  const queryWords = query.trim().split(/\s+/);
  let highlightedText = text;

  queryWords.forEach((word) => {
    if (word.length >= 2) {
      // Reduced from 3 to 2 for better matching
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escapedWord})`, "gi");
      highlightedText = highlightedText.replace(regex, '<mark class="enhanced-search-highlight">$1</mark>');
    }
  });

  if (highlightedText !== text) {
    console.log("[Enhanced Search] Highlighted:", { original: text.substring(0, 50), query });
  }

  return highlightedText;
}

/**
 * Remove configured title prefix if set
 */
function formatDisplayTitle(title) {
  if (ENHANCED_SEARCH_CONFIG.titlePrefix && title.startsWith(ENHANCED_SEARCH_CONFIG.titlePrefix)) {
    return title.substring(ENHANCED_SEARCH_CONFIG.titlePrefix.length);
  }
  return title;
}

const PRODUCT_TITLE_SELECTORS = [
  ".card__title",
  ".card__heading",
  ".card-product__title",
  ".product-card__title",
  ".predictive-search__item-heading",
  "h3[class*='title']",
  "h4[class*='title']",
  ".card__info h3",
  ".card__info h4",
  ".card__content h3",
  ".card__content h4",
].join(", ");

function formatProductMoney(value) {
  const amount = parseFloat(value) || 0;
  return `$${amount.toFixed(2)}`;
}

function buildProductImageUrl(imageUrl, width = 600) {
  if (!imageUrl) return "";
  const separator = imageUrl.includes("?") ? "&" : "?";
  return `${imageUrl}${separator}width=${width}`;
}

function inferProductMpn(product) {
  if (typeof product.inventory_number === "string" && product.inventory_number.trim()) {
    return product.inventory_number.trim();
  }

  const primarySku = typeof product.sku === "string" ? product.sku.trim() : "";
  if (!primarySku) return "";

  return primarySku.replace(/^[^\d]+/, "").replace(/x\d+$/i, "");
}

function getTitleLinkTarget(titleElement) {
  if (!titleElement) return null;

  const existingTitleLink =
    titleElement.closest("a[href*='/products/']") ||
    titleElement.querySelector("a[href*='/products/']");
  if (existingTitleLink) return existingTitleLink;

  const productContainer =
    titleElement.closest(".card, .card-wrapper, .product-card, .f-column, li, .predictive-search__item, .predictive-search__list-item") ||
    titleElement.parentElement;

  if (!productContainer) return null;

  return (
    Array.from(productContainer.querySelectorAll("a[href*='/products/']")).find(
      (link) => !titleElement.contains(link),
    ) || null
  );
}

function ensureTitleElementIsLinked(titleElement, productUrl = null, productTitle = null) {
  if (!titleElement) return;

  const targetLink = getTitleLinkTarget(titleElement);
  const resolvedUrl = productUrl || targetLink?.getAttribute("href") || targetLink?.href;
  if (!resolvedUrl) return;

  if (targetLink && (titleElement.closest("a") === targetLink || titleElement.querySelector("a") === targetLink)) {
    targetLink.href = resolvedUrl;
    targetLink.setAttribute("href", resolvedUrl);
    if (productTitle) targetLink.setAttribute("aria-label", productTitle);
    return;
  }

  const titleLink = document.createElement("a");
  titleLink.href = resolvedUrl;
  titleLink.setAttribute("href", resolvedUrl);
  if (productTitle) titleLink.setAttribute("aria-label", productTitle);
  titleLink.className = "full-unstyled-link";
  titleLink.innerHTML = titleElement.innerHTML;
  titleElement.innerHTML = "";
  titleElement.appendChild(titleLink);
}

function ensureProductTitleLinks(root) {
  if (!root) return;

  root.querySelectorAll(PRODUCT_TITLE_SELECTORS).forEach((titleElement) => {
    ensureTitleElementIsLinked(titleElement);
  });
}

function buildFoxThemeFallbackProductCard(product, query) {
  const displayTitle = formatDisplayTitle(product.title);
  const highlightedTitle = highlightSearchText(displayTitle, query);
  const price = parseFloat(product.price) || 0;
  const compareAtPrice = parseFloat(product.compare_at_price) || 0;
  const hasDiscount = compareAtPrice > price;
  const imageUrl = buildProductImageUrl(product.image, 300);

  return `
          <div class="f-column" style="--f-column-span: 2; --f-column-span-mobile: 6;">
            <div class="card card--product">
              <a href="/products/${product.handle}" class="card__link full-width-link">
                <span class="visually-hidden">${product.title}</span>
              </a>
              <div class="card__media">
                <div class="media media--portrait media--hover-effect" style="--media-aspect-ratio: 1">
                  <img src="${imageUrl}" alt="${product.title}" loading="lazy" width="150" height="150" class="motion-reduce">
                </div>
              </div>
              <div class="card__content">
                <div class="card__info">
                  <h3 class="card__title h6"><a href="/products/${product.handle}" class="full-unstyled-link" aria-label="${product.title}">${highlightedTitle}</a></h3>
                  <div class="card__price">
                    <div class="price ${!product.available ? "price--sold-out" : ""} ${hasDiscount ? "price--on-sale" : ""}">
                      <span class="price__regular">${formatProductMoney(product.price)}</span>
                      ${hasDiscount ? `<span class="price__compare"><s>${formatProductMoney(product.compare_at_price)}</s></span>` : ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
}

function buildFoxThemeProductCardHtml(templateItem, product, query) {
  if (!templateItem) {
    return buildFoxThemeFallbackProductCard(product, query);
  }

  const clone = templateItem.cloneNode(true);
  clone.removeAttribute("id");
  clone.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));

  const productUrl = `/products/${product.handle}`;
  clone.querySelectorAll("a[href*='/products/']").forEach((link) => {
    link.href = productUrl;
    link.setAttribute("href", productUrl);
    link.setAttribute("aria-label", product.title);
  });

  const titleElement = clone.querySelector(PRODUCT_TITLE_SELECTORS);
  if (titleElement) {
    titleElement.innerHTML = highlightSearchText(formatDisplayTitle(product.title), query);

    ensureTitleElementIsLinked(titleElement, productUrl, product.title);
  }

  clone.querySelectorAll("source").forEach((element) => element.remove());
  const imageElements = Array.from(clone.querySelectorAll("img"));
  const primaryImage = imageElements[0];
  if (primaryImage) {
    const imageUrl = buildProductImageUrl(product.image, 600);
    if (imageUrl) {
      primaryImage.src = imageUrl;
      primaryImage.setAttribute("src", imageUrl);
    }
    primaryImage.removeAttribute("srcset");
    primaryImage.removeAttribute("data-src");
    primaryImage.removeAttribute("data-widths");
    primaryImage.setAttribute("alt", product.title);
    primaryImage.alt = product.title;
  }
  imageElements.slice(1).forEach((element) => element.remove());

  const regularPriceItem = clone.querySelector(".price-item--regular");
  if (regularPriceItem) {
    regularPriceItem.textContent = formatProductMoney(product.price);
  } else {
    const regularPriceContainer = clone.querySelector(".price__regular");
    if (regularPriceContainer) {
      regularPriceContainer.innerHTML = `<span class="price-item price-item--regular">${formatProductMoney(product.price)}</span>`;
    }
  }

  const hasDiscount = (parseFloat(product.compare_at_price) || 0) > (parseFloat(product.price) || 0);
  const comparePriceWrapper = clone.querySelector(".price__compare, .price-item--sale, .price-item--compare");
  if (hasDiscount && comparePriceWrapper) {
    const compareTarget = comparePriceWrapper.querySelector("s") || comparePriceWrapper;
    compareTarget.textContent = formatProductMoney(product.compare_at_price);
    comparePriceWrapper.classList.remove("hidden");
  } else if (comparePriceWrapper) {
    comparePriceWrapper.remove();
  }

  const priceWrapper = clone.querySelector(".price");
  if (priceWrapper) {
    priceWrapper.classList.toggle("price--on-sale", hasDiscount);
    priceWrapper.classList.toggle("price--sold-out", !product.available);
  }

  const metadataElement = Array.from(clone.querySelectorAll("*"))
    .filter((element) => {
      const text = (element.textContent || "").replace(/\s+/g, " ").trim();
      return text.length > 0 && text.length < 160 && (text.includes("SKU #") || text.includes("MPN #"));
    })
    .sort((a, b) => a.textContent.length - b.textContent.length)[0];
  if (metadataElement) {
    const sku = typeof product.sku === "string" ? product.sku.trim() : "";
    const mpn = inferProductMpn(product);
    metadataElement.innerHTML = `SKU #:<br>${sku}${mpn ? ` | <br>MPN #: ${mpn}` : ""}`;
  }

  const availabilityElement = Array.from(clone.querySelectorAll("*"))
    .filter((element) => {
      const text = (element.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      return (
        text.length > 0 &&
        text.length < 160 &&
        (text.includes("available in stock") || text.includes("special order") || text.includes("out of stock"))
      );
    })
    .sort((a, b) => a.textContent.length - b.textContent.length)[0];
  if (availabilityElement) {
    availabilityElement.innerHTML = product.available
      ? "Available in<br>stock"
      : "Special Order.<br>Expect 4-6 weeks<br>for delivery,<br>subject to change";
  }

  return clone.outerHTML;
}

/**
 * Detect theme type based on available globals and DOM structure
 */
function detectThemeType() {
  if (typeof FoxTheme !== "undefined" && FoxTheme.routes) {
    return "foxtheme";
  }
  if (typeof SearchForm !== "undefined") {
    return "dawn";
  }
  // Check for common Dawn elements
  if (document.querySelector("[data-predictive-search]")) {
    return "dawn";
  }
  // Default to foxtheme-style if we detect their structure
  if (document.querySelector(".search__content")) {
    return "foxtheme";
  }
  return "unknown";
}

const THEME_TYPE = detectThemeType();
console.log(`[Enhanced Search] Detected theme type: ${THEME_TYPE}`);

// ============================================
// FoxTheme-based Implementation
// ============================================
if (THEME_TYPE === "foxtheme" || THEME_TYPE === "unknown") {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedMap = new Map();
      this.focusElement = this.input;
      this.resetButton.addEventListener("click", this.clear.bind(this));
      this.input.addEventListener("input", this.debounce(this.onChange.bind(this), 300));
      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.searchContent = this.querySelector(".search__content");
      this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === "true";
      this.header = document.querySelector("header");
      this.toggleNavigationButton = document.querySelector(".toggle-navigation-button");

      this.boundHandleClickOutside = this.handleClickOutside.bind(this);
      document.addEventListener("click", this.boundHandleClickOutside);
      this.searchProductTypes?.addEventListener("change", this.handleProductTypeChange.bind(this));

      this.isHeaderSearch = this.closest("header") !== null;

      document.addEventListener("menu-drawer:open", () => {
        this.classList.remove("predictive-search-open");
        document.body.classList.remove("search-open");
      });

      this.states = {
        OPEN: "predictive-search-open",
        LOADING: "btn--loading",
        SEARCH_OPEN: "search-open",
      };
    }

    // Simple debounce implementation
    debounce(fn, delay) {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
      };
    }

    // Get section ID from element
    getSectionId() {
      // Try FoxTheme utility first
      if (typeof FoxTheme !== "undefined" && FoxTheme.utils?.getSectionId) {
        return FoxTheme.utils.getSectionId(this);
      }
      // Fallback: extract from form ID or generate from element
      const form = this.querySelector("form");
      if (form && form.id) {
        const match = form.id.match(/PredictiveSearch-(.+)/);
        if (match) return match[1];
      }
      // Last resort: use a hash of the element
      return this.id || "main";
    }

    get input() {
      return this.querySelector('input[type="search"]');
    }

    get resetButton() {
      return this.querySelector('button[type="reset"]');
    }

    get searchProductTypes() {
      return this.querySelector("#SearchProductTypes");
    }

    onFocus(event) {
      document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      if (this.header) {
        document.documentElement.style.setProperty(
          "--header-bottom-position",
          `${parseInt(this.header.getBoundingClientRect().bottom)}px`,
        );
      }
      if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
        document.body.classList.add("search-open");
        this.toggleNavButtonVisibility(false);
      }

      if (!this.searchRecommendationEmpty) {
        this.searchContent?.classList.remove("hidden");
      }
      this.classList.add("predictive-search-open");
      if (this.getQuery().length === 0) {
        if (this.searchRecommendationEmpty) {
          this.searchContent?.classList.add("hidden");
        }
        return;
      }
      this.performSearch();
    }

    getQuery() {
      return this.input?.value.trim() || "";
    }

    clear(event = null) {
      event?.preventDefault();
      this.input.value = "";
      this.input.focus();
      this.removeAttribute("results");
      this.toggleSearchState(false);
    }

    handleProductTypeChange(evt) {
      const query = this.getQuery();
      if (query.length > 0) {
        this.performSearch();
      }
    }

    getShopifySearchUrl() {
      // Try FoxTheme routes first
      const baseUrl =
        typeof FoxTheme !== "undefined" && FoxTheme.routes?.predictive_search_url
          ? FoxTheme.routes.predictive_search_url
          : "/search/suggest";

      const shopUrl = window.shopUrl || "";
      const url = new URL(`${shopUrl}${baseUrl}`, window.location.origin);

      let search_term = this.getQuery();
      if (this.searchProductTypes && this.searchProductTypes.value !== "") {
        search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
      }

      url.searchParams.set("q", search_term);
      url.searchParams.set("resources[limit]", this.dataset.resultsLimit || "6");
      url.searchParams.set("resources[limit_scope]", "each");
      url.searchParams.set("section_id", this.getSectionId());

      return url.toString();
    }

    onChange() {
      if (this.getQuery().length === 0) {
        this.clear();
        return;
      }
      this.performSearch();
    }

    async performSearch() {
      const query = this.getQuery();
      const cacheKey = query.toLowerCase();

      if (this.cachedMap.has(cacheKey)) {
        this.renderSearchResults(this.cachedMap.get(cacheKey));
        return;
      }

      this.setLoadingState(true);

      try {
        // Build requests array
        const shopifyUrl = this.getShopifySearchUrl();
        const requests = [fetch(shopifyUrl)];

        // Add enhanced search if active; disabled the fuzzy search for now
        // if (ENHANCED_SEARCH_CONFIG.active) {
        //   requests.push(
        //     fetch(buildEnhancedSearchUrl(query), {
        //       headers: { Accept: "application/json" },
        //     }),
        //   );
        // }

        const responses = await Promise.all(requests);
        const shopifyText = await responses[0].text();
        // const fuzzyProducts = ENHANCED_SEARCH_CONFIG.active ? await responses[1].json() : { products: [] }; //remove this to reactivate fuzzy search

        // Parse Shopify response and inject enhanced results
        const enhancedHtml = this.mergeSearchResults(shopifyText, fuzzyProducts, query);

        this.cachedMap.set(cacheKey, enhancedHtml);
        this.renderSearchResults(enhancedHtml);
      } catch (error) {
        console.error("[Enhanced Search] Error:", error);
        this.setAttribute("error", "Failed to load data");
      } finally {
        this.setLoadingState(false);
      }
    }

    mergeSearchResults(shopifyHtml, fuzzyData, query) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(shopifyHtml, "text/html");
      const sectionId = this.getSectionId();
      const resultsId = `PredictiveSearchResults-${sectionId}`;
      const targetElement = doc.getElementById(resultsId);

      if (!targetElement || !fuzzyData.products || fuzzyData.products.length === 0) {
        return shopifyHtml;
      }

      // Find the products container - try multiple selectors for different theme variants
      let productsList =
        targetElement.querySelector("#predictive-search-results-products-list") ||
        targetElement.querySelector(".predictive-search-result--products .f-grid") ||
        targetElement.querySelector(".search__box-products .f-grid");

      // If Shopify returned no results, the products container won't exist.
      // Create the full results structure so we can inject our enhanced results.
      if (!productsList) {
        console.log("[Enhanced Search] No products container found - creating one for enhanced results");

        // Clear any "no results" empty state
        targetElement.innerHTML = "";

        // Build the same structure the Liquid template would render
        const wrapper = document.createElement("div");
        wrapper.className = "flex w-full flex-col gap-y-6 md:flex-row";
        wrapper.innerHTML = `
          <div class="flex flex-col flex-grow search__box-products order-last md:order-first">
            <div class="flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products">
              <div class="swipe-mobile swipe-mobile--2-cols">
                <div id="predictive-search-results-products-list" class="f-grid swipe-mobile__inner"></div>
              </div>
            </div>
          </div>
        `;
        targetElement.appendChild(wrapper);
        productsList = targetElement.querySelector("#predictive-search-results-products-list");
      }

      const existingItems = Array.from(productsList.children);

      // Extract existing product handles to avoid duplicates
      const existingHandles = new Set();
      productsList.querySelectorAll("a[href*='/products/']").forEach((link) => {
        const match = link.href.match(/\/products\/([^/?]+)/);
        if (match) existingHandles.add(match[1]);
      });

      // Filter out duplicates from fuzzy results
      const uniqueFuzzyProducts = fuzzyData.products.filter((product) => !existingHandles.has(product.handle));

      if (uniqueFuzzyProducts.length === 0) {
        return shopifyHtml;
      }

      const templateItem = existingItems[0] || null;
      const fuzzyResultsHTML = uniqueFuzzyProducts
        .map((product) => buildFoxThemeProductCardHtml(templateItem, product, query))
        .join("");

      // Prepend fuzzy results to the product list
      productsList.insertAdjacentHTML("afterbegin", fuzzyResultsHTML);

      // Apply highlighting to existing Shopify results only.
      // The enhanced cards are already highlighted when we build them, and
      // re-highlighting the whole list strips the title anchors we injected.
      if (ENHANCED_SEARCH_CONFIG.highlightEnabled) {
        existingItems.forEach((item) => {
          item.querySelectorAll(PRODUCT_TITLE_SELECTORS).forEach((el) => {
            const text = el.textContent;
            if (text) {
              el.innerHTML = highlightSearchText(text, query);
            }
          });
        });
      }

      return doc.body.innerHTML;
    }

    renderSearchResults(responseText) {
      const sectionId = this.getSectionId();
      const id = `PredictiveSearchResults-${sectionId}`;
      const targetElement = document.getElementById(id);

      if (targetElement) {
        const parser = new DOMParser();
        const parsedDoc = parser.parseFromString(responseText, "text/html");
        const contentElement = parsedDoc.getElementById(id);

        if (contentElement) {
          this.searchContent?.classList.remove("hidden");
          targetElement.innerHTML = contentElement.innerHTML;
          ensureProductTitleLinks(targetElement);

          if (this.isHeaderSearch && !document.body.classList.contains("search-open")) {
            document.body.classList.add("search-open");
            this.toggleNavButtonVisibility(true);
            this.classList.add("predictive-search-open");
          } else {
            this.classList.add("predictive-search-open");
          }
        }
      }
    }

    handleClickOutside(event) {
      const target = event.target;
      const shouldClose = this.isHeaderSearch
        ? !this.contains(target) &&
          ((target.classList.contains("fixed-overlay") && target.closest(".header-section")) ||
            target.classList.contains("header__search-close"))
        : !this.contains(target);

      if (shouldClose) {
        setTimeout(() => this.toggleSearchState(false));
      }
    }

    toggleSearchState(isOpen) {
      this.classList.toggle(this.states.OPEN, isOpen);
      if (this.isHeaderSearch) {
        document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
        this.toggleNavButtonVisibility(!isOpen);
      }
      if (!isOpen && this.searchRecommendationEmpty) {
        this.searchContent?.classList.add("hidden");
      }
    }

    toggleNavButtonVisibility(isOpen) {
      this.toggleNavigationButton?.classList.toggle("is-show", isOpen);
    }

    setLoadingState(isLoading) {
      if (isLoading) {
        this.setAttribute("loading", "true");
        this.resetButton?.classList.add(this.states.LOADING);
      } else {
        this.removeAttribute("loading");
        this.resetButton?.classList.remove(this.states.LOADING);
        this.setAttribute("results", "true");
      }
    }
  }

  customElements.define("predictive-search", PredictiveSearch);
}

// ============================================
// Dawn-based Implementation (original)
// ============================================
else if (THEME_TYPE === "dawn") {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
      this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = "";

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.addEventListener("focusout", this.onFocusOut.bind(this));
      this.addEventListener("keyup", this.onKeyup.bind(this));
      this.addEventListener("keydown", this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
      }

      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = "";
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute("results") === "true") {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case "ArrowUp":
          this.switchOption("up");
          break;
        case "ArrowDown":
          this.switchOption("down");
          break;
        case "Enter":
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      if (event.code === "ArrowUp" || event.code === "ArrowDown") {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
      const currentButtonText = searchForTextElement?.innerText;
      if (currentButtonText) {
        if (currentButtonText.match(new RegExp(previousTerm, "g"))?.length > 1) {
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      }
    }

    switchOption(direction) {
      if (!this.getAttribute("open")) return;

      const moveUp = direction === "up";
      const selectedElement = this.querySelector('[aria-selected="true"]');

      const allVisibleElements = Array.from(this.querySelectorAll("li, button.predictive-search__item")).filter(
        (element) => element.offsetParent !== null,
      );
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = "";

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute("aria-selected", true);
      if (selectedElement) selectedElement.setAttribute("aria-selected", false);

      this.input.setAttribute("aria-activedescendant", activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selectedOption) selectedOption.click();
    }

    async getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const requests = [
          fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
            signal: this.abortController.signal,
          }),
        ];

        if (ENHANCED_SEARCH_CONFIG.active) {
          requests.push(
            fetch(buildEnhancedSearchUrl(searchTerm), {
              signal: this.abortController.signal,
              headers: { Accept: "application/json" },
            }),
          );
        }

        const responses = await Promise.all(requests);

        const shopifyText = await responses[0].text();
        const fuzzyProducts = ENHANCED_SEARCH_CONFIG.active ? await responses[1].json() : { products: [] };

        const parser = new DOMParser();
        const doc = parser.parseFromString(shopifyText, "text/html");
        const resultsContainer = doc.querySelector("#shopify-section-predictive-search");

        const productsList = resultsContainer?.querySelector("#predictive-search-results-products-list");

        if (productsList && fuzzyProducts.products && fuzzyProducts.products.length > 0) {
          const existingItems = Array.from(productsList.children);
          const shopifyProductHandles = new Set();

          existingItems.forEach((item) => {
            const linkElement = item.querySelector("a");
            if (linkElement && linkElement.href) {
              const url = new URL(linkElement.href, window.location.origin);
              const pathSegments = url.pathname.split("/");
              if (pathSegments.length >= 3 && pathSegments[1] === "products") {
                shopifyProductHandles.add(pathSegments[2]);
              }
            }
          });

          const uniqueFuzzyProducts = fuzzyProducts.products.filter(
            (product) => !shopifyProductHandles.has(product.handle),
          );

          const fuzzyResultsHTML = uniqueFuzzyProducts
            .map((product, index) => {
              const displayTitle = formatDisplayTitle(product.title);
              const highlightedTitle = highlightSearchText(displayTitle, searchTerm);
              const price = parseFloat(product.price) || 0;
              const priceMin = parseFloat(product.price_min) || 0;
              const priceMax = parseFloat(product.price_max) || 0;
              const compareAtPrice = parseFloat(product.compare_at_price) || 0;

              return `
              <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
                <a href="/products/${product.handle}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
                  <img class="predictive-search__image" loading="lazy"
                       src="${product.image}&width=150"
                       width="56" height="56"
                       alt="${product.title}">
                  <div class="predictive-search__item-content">
                    <p class="predictive-search__item-heading h5">
                      ${highlightedTitle}
                    </p>
                    <div class="price ${!product.available ? "price--sold-out" : ""} ${compareAtPrice > price ? "price--on-sale" : ""}">
                      <div class="price__container">
                        <div class="price__regular">
                          <span class="visually-hidden visually-hidden--inline">Regular price</span>
                          <span class="price-item price-item--regular">
                            ${priceMin < priceMax ? '<span class="text--small subdued">From</span> ' : ""}
                            $${price}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </li>
            `;
            })
            .join("");

          const updatedExistingItemsHTML = existingItems
            .map((item, index) => {
              const clone = item.cloneNode(true);
              clone.id = `predictive-search-option-product-${index + uniqueFuzzyProducts.length + 1}`;

              if (ENHANCED_SEARCH_CONFIG.highlightEnabled) {
                const titleElement = clone.querySelector(".predictive-search__item-heading");
                if (titleElement) {
                  const text = titleElement.textContent;
                  if (text) {
                    titleElement.innerHTML = highlightSearchText(text, searchTerm);
                  }
                }
              }

              return clone.outerHTML;
            })
            .join("");

          productsList.innerHTML = fuzzyResultsHTML + updatedExistingItemsHTML;
        }

        const resultsMarkup = resultsContainer?.innerHTML || shopifyText;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      } catch (error) {
        if (error?.code === 20) return;
        this.close();
        throw error;
      }
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
      this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
      this.setLiveRegionText(this.loadingText);
      this.setAttribute("loading", true);
    }

    setLiveRegionText(statusText) {
      this.statusElement?.setAttribute("aria-hidden", "false");
      if (this.statusElement) this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement?.setAttribute("aria-hidden", "true");
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute("results", true);
      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute("loading");
      const countElement = this.querySelector("[data-predictive-search-live-region-count-value]");
      if (countElement) {
        this.setLiveRegionText(countElement.textContent);
      }
    }

    getResultsMaxHeight() {
      const header = document.querySelector(".section-header");
      this.resultsMaxHeight = header
        ? window.innerHeight - header.getBoundingClientRect().bottom
        : window.innerHeight - 100;
      return this.resultsMaxHeight;
    }

    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute("open", true);
      this.input.setAttribute("aria-expanded", true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = "";
        this.removeAttribute("results");
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute("aria-selected", false);

      this.input.setAttribute("aria-activedescendant", "");
      this.removeAttribute("loading");
      this.removeAttribute("open");
      this.input.setAttribute("aria-expanded", false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults?.removeAttribute("style");
    }
  }

  customElements.define("predictive-search", PredictiveSearch);
}

/**
 * Add CSS for highlighting (injected once)
 */
(function injectHighlightStyles() {
  if (document.getElementById("enhanced-search-styles")) return;

  const style = document.createElement("style");
  style.id = "enhanced-search-styles";
  style.textContent = `
    mark.enhanced-search-highlight,
    .enhanced-search-highlight {
      background-color: rgba(255, 235, 59, 0.4) !important;
      color: inherit !important;
      padding: 0 2px !important;
      border-radius: 2px;
      font-weight: inherit;
      display: inline;
    }
  `;
  document.head.appendChild(style);
})();

// Log config for debugging
console.log("[Enhanced Search] Config:", {
  active: ENHANCED_SEARCH_CONFIG.active,
  clientId: ENHANCED_SEARCH_CONFIG.clientId,
  highlightEnabled: ENHANCED_SEARCH_CONFIG.highlightEnabled,
});
